import React from 'react'
import {
  AnimatedCheckMark,
  Button,
  Flex,
  Input,
  Spacer,
  LoadingSpinner,
  Text,
} from '@sendoutcards/quantum-design-ui'
import {
  VerifyPhoneNumberSuccessFragment,
  VerifyPhoneNumberSuccessType,
} from 'src/graphql/generated/graphql'
import { useUpdateAccount } from 'src/react_query'
import { useState } from 'src/hooks'

const VerifyPhoneNumberSuccess = (props: {
  success: VerifyPhoneNumberSuccessFragment
  onCompletion: (success: VerifyPhoneNumberSuccessFragment) => void
  didVerifyExistingAccount: boolean
}) => {
  const {
    success: { successType, verifiedAccount },
    onCompletion,
    didVerifyExistingAccount,
  } = props
  const { mutate, isLoading, error } = useUpdateAccount()
  const [formData, setFormData] = useState({ firstName: '', lastName: '' })
  const message = (() => {
    switch (successType) {
      case VerifyPhoneNumberSuccessType.CreatedNewAccount:
        return {
          title: 'You did it!',
          description:
            'You’ve activated your free card and are on your way to sending kindness to anyone, anywhere in the world! Start thinking of who you want to send a card to!',
        }
      case VerifyPhoneNumberSuccessType.UpdatedExistingAccount:
        return {
          title: 'Update my existing account info',
          description:
            'Your phone number has been verified and updated in your account',
        }
      case VerifyPhoneNumberSuccessType.SignedIntoExistingAccount:
        return didVerifyExistingAccount
          ? {
              title: 'Phone Number Verified!',
              description:
                'Your phone number has been successfully verified. You will now be able to log in to your account using just your phone number!',
            }
          : {
              title: `Welcome back${
                verifiedAccount.firstName ? ` ${verifiedAccount.firstName}` : ''
              }!`,
              description:
                'It looks like you started creating a card - would you like to finish it?',
            }
    }
  })()

  if (!verifiedAccount.firstName || !verifiedAccount.lastName) {
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault()
      const success = {
        ...props.success,
        firstName: formData.firstName,
        lastName: formData.lastName,
      }
      props.onCompletion(success)
      mutate({
        account: {
          firstName: formData.firstName,
          lastName: formData.lastName,
        },
      })
    }

    return (
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <Spacer space="x4" />
        <Flex
          justifyContent={'center'}
          alignItems={'center'}
          flexDirection={'column'}
          width={'100%'}
        >
          <Text type="footnote" weight="bold">
            Complete Your Profile
          </Text>
          <Spacer space="x2" />
          <Text type="caption" alignment={'center'}>
            Please provide your first and last name to continue
          </Text>
          <Spacer space="x4" />
        </Flex>
        <Flex flexDirection="column" style={{ gap: '24px' }}>
          <Input
            type="text"
            labelPlaceholder="First Name"
            isFullWidth
            value={formData.firstName}
            onChange={value =>
              setFormData(prev => ({ ...prev, firstName: value }))
            }
            message={
              error
                ? {
                    type: 'danger',
                    content:
                      error instanceof Error
                        ? error.message
                        : 'Failed to update account',
                  }
                : undefined
            }
            isDisabled={isLoading}
          />
          <Input
            type="text"
            labelPlaceholder="Last Name"
            isFullWidth
            value={formData.lastName}
            onChange={value =>
              setFormData(prev => ({ ...prev, lastName: value }))
            }
            message={
              error
                ? {
                    type: 'danger',
                    content:
                      error instanceof Error
                        ? error.message
                        : 'Failed to update account',
                  }
                : undefined
            }
            isDisabled={isLoading}
          />
        </Flex>

        <Spacer space="x4" />
        <Flex justifyContent="center">
          <Button
            onClick="submitForm"
            size="large"
            disabled={
              isLoading ||
              !formData.firstName.trim() ||
              !formData.lastName.trim()
            }
          >
            {isLoading ? <LoadingSpinner size="small" /> : 'Continue'}
          </Button>
        </Flex>
      </form>
    )
  }

  return (
    <form onSubmit={() => onCompletion(props.success)}>
      <Spacer space="x4" />
      <Flex
        justifyContent={'center'}
        alignItems={'center'}
        flexDirection={'column'}
      >
        <AnimatedCheckMark size={'large'} />
        <Spacer space="x3" />
        <Text type="footnote" weight="bold">
          {message.title}
        </Text>
        <Spacer space="x2" />
        <Text type="caption" alignment={'center'}>
          {message.description}
        </Text>
        <Spacer space="x4" />
      </Flex>
      <Spacer space="x2" />
      <Flex justifyContent="center">
        <Button
          title={
            didVerifyExistingAccount
              ? 'Continue to account'
              : 'Continue to card catalog'
          }
          onClick="submitForm"
          size="large"
        />
      </Flex>
    </form>
  )
}

export default VerifyPhoneNumberSuccess
